import { useEffect, useMemo, useState } from "react";
import { Edit3, Plus, Search, Trash2, UserRoundPlus } from "lucide-react";
import type { EmailContact, User } from "../types";
import { api } from "../lib/api";
import { Avatar, Badge, Button, EmptyState, IconButton, Loading, Modal } from "../components/ui";

const blankContact = { name: "", emailAddress: "", company: "" };

export default function ContactsPage({
  user,
  notify
}: {
  user: User;
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
}) {
  const [contacts, setContacts] = useState<EmailContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<EmailContact | "new" | null>(null);
  const [form, setForm] = useState(blankContact);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setContacts(await api.contacts());
    } catch (error) {
      notify("error", "Couldn’t load contacts", (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return contacts.filter((contact) => !term ||
      contact.name.toLowerCase().includes(term) ||
      contact.emailAddress.toLowerCase().includes(term) ||
      contact.company?.toLowerCase().includes(term));
  }, [contacts, search]);

  const openEditor = (contact?: EmailContact) => {
    if (contact) {
      setEditing(contact);
      setForm({
        name: contact.name,
        emailAddress: contact.emailAddress,
        company: contact.company ?? ""
      });
    } else {
      setEditing("new");
      setForm(blankContact);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.createContact(form);
      else if (editing?.id) await api.updateContact(editing.id, form);
      setEditing(null);
      notify("success", editing === "new" ? "Contact added" : "Contact updated");
      await load();
    } catch (error) {
      notify("error", "Couldn’t save contact", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const saveDiscovered = async (contact: EmailContact) => {
    try {
      await api.createContact({
        name: contact.name,
        emailAddress: contact.emailAddress,
        company: contact.company ?? ""
      });
      notify("success", "Contact saved");
      await load();
    } catch (error) {
      notify("error", "Couldn’t save contact", (error as Error).message);
    }
  };

  const remove = async (contact: EmailContact) => {
    if (!contact.id || !window.confirm(`Remove ${contact.name} from shared contacts?`)) return;
    try {
      await api.deleteContact(contact.id);
      notify("success", "Contact removed");
      await load();
    } catch (error) {
      notify("error", "Couldn’t remove contact", (error as Error).message);
    }
  };

  return (
    <div className="page contacts-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Shared address book</span>
          <h1>Contacts</h1>
          <p>Saved contacts and people discovered from company conversations.</p>
        </div>
        {user.isAdministrator && (
          <Button icon={<Plus size={17} />} onClick={() => openEditor()}>Add contact</Button>
        )}
      </div>
      <label className="search-field contacts-search">
        <Search size={16} />
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search contacts" />
      </label>
      {loading ? (
        <Loading label="Loading contacts…" />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<UserRoundPlus />} title="No contacts found" message="Add a shared contact or exchange an email to discover one." />
      ) : (
        <div className="contact-list">
          {filtered.map((contact) => (
            <article key={contact.id ?? contact.emailAddress}>
              <Avatar name={contact.name} tone={contact.emailAddress.length} />
              <span>
                <strong>{contact.name}</strong>
                <small>{contact.emailAddress}{contact.company ? ` · ${contact.company}` : ""}</small>
              </span>
              <Badge tone={contact.isSaved ? "success" : "neutral"}>
                {contact.isSaved ? "Shared" : "Discovered"}
              </Badge>
              {user.isAdministrator && (
                <div>
                  {contact.isSaved ? (
                    <>
                      <IconButton label="Edit contact" onClick={() => openEditor(contact)}><Edit3 size={16} /></IconButton>
                      <IconButton label="Remove contact" onClick={() => remove(contact)}><Trash2 size={16} /></IconButton>
                    </>
                  ) : (
                    <Button tone="ghost" icon={<Plus size={15} />} onClick={() => saveDiscovered(contact)}>Save</Button>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "Add shared contact" : "Edit contact"}
      >
        <div className="stack-form">
          <label className="field"><span>Name</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
          <label className="field"><span>Email address</span><input type="email" value={form.emailAddress} onChange={(event) => setForm({ ...form, emailAddress: event.target.value })} /></label>
          <label className="field"><span>Company (optional)</span><input value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} /></label>
        </div>
        <div className="modal-footer">
          <Button tone="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save contact"}</Button>
        </div>
      </Modal>
    </div>
  );
}
